import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Sparkles, QrCode, Smartphone, Moon, Sun, BarChart3, Package,
  Layers, CalendarRange, Truck, Shield, ChevronRight, ChevronLeft, Check
} from 'lucide-react';

const UPDATE_VERSION = 'v2.5.0-apr2026';
const DISMISSED_KEY = `update_dismissed_${UPDATE_VERSION}`;

export default function HotfixUpdatePopup({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(0); // 0 = changelog, 1 = QR tutorial
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Show every login/page-load until the user explicitly ticks "Don't show again"
    if (localStorage.getItem(DISMISSED_KEY) === 'true') return;
    setIsOpen(true);
    setDontShowAgain(false); // reset each time the popup reopens
  }, [user]);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(DISMISSED_KEY, 'true');
    }
    setIsOpen(false);
  };

  if (!isOpen) return null;

  const updates = [
    {
      icon: <QrCode className="h-5 w-5" />,
      title: "QR Code Passwordless Login",
      color: 'var(--amber)',
      bg: 'var(--amber-glow)',
      items: [
        "Scan the QR code on the login page with your phone to sign in on desktop — no typing needed",
        "First scan registers your phone (one-time password entry)",
        "All subsequent scans are instant & passwordless",
        "Manage registered devices in Settings with one-tap revoke",
      ],
    },
    {
      icon: <Moon className="h-5 w-5" />,
      title: "Dark / Light Theme Toggle",
      color: 'var(--blue)',
      bg: 'var(--blue-dim)',
      items: [
        "Switch between Dark mode (RouteOps) and Light mode in Settings → Theme",
        "Your preference is saved and persists across sessions",
      ],
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      title: "Picker Performance Reporting",
      color: 'var(--green)',
      bg: 'var(--green-dim)',
      items: [
        "New Reporting page in RouteOps sidebar",
        "See which orders each picker handled, how long each pick took, and daily averages",
        "Filter by date range for historical analysis",
      ],
    },
    {
      icon: <Package className="h-5 w-5" />,
      title: "Picker Assignment & Timestamps",
      color: 'var(--amber)',
      bg: 'var(--amber-glow)',
      items: [
        "Assign a picker to each booking from the full user list (drivers shown first)",
        "Pick Start timestamp auto-recorded on booking creation",
        "Pick Finish timestamp recorded when picking is marked complete",
        "Weight field is now optional at booking (update later from Terminal)",
      ],
    },
    {
      icon: <Truck className="h-5 w-5" />,
      title: "Terminal — Weight Lookup & Update",
      color: 'var(--blue)',
      bg: 'var(--blue-dim)',
      items: [
        "Search any booking by business name directly in Terminal",
        "Update weight after palletisation without editing the booking",
      ],
    },
    {
      icon: <CalendarRange className="h-5 w-5" />,
      title: "Date Range Picker on Deliveries",
      color: 'var(--green)',
      bg: 'var(--green-dim)',
      items: [
        "New From / To date inputs alongside the existing period filter",
        "Delivery dates now use UK timezone so today's deliveries always show correctly",
      ],
    },
    {
      icon: <Sparkles className="h-5 w-5" />,
      title: "Route Capacity Heatmap",
      color: 'var(--amber)',
      bg: 'var(--amber-glow)',
      items: [
        "Weekly Planner route cells now show green/amber/red background based on capacity utilisation",
        "Mini capacity progress bar per route for quick visual scanning",
      ],
    },
    {
      icon: <Layers className="h-5 w-5" />,
      title: "Backend Modularisation & Performance",
      color: 'var(--text-muted)',
      bg: 'var(--bg-raised)',
      items: [
        "server.py split from 3,600 lines into 11 modular route files — faster, cleaner, easier to maintain",
        "N+1 database queries eliminated, bulk writes for auto-plan",
        "AI API keys encrypted at rest (Fernet), overweight auto-reconciliation",
      ],
    },
  ];

  const tutorialSteps = [
    {
      step: 1,
      title: "Go to Settings",
      desc: "Log in normally, then navigate to RouteOps → Settings (gear icon at the bottom of the sidebar).",
      icon: <Shield className="h-8 w-8" />,
    },
    {
      step: 2,
      title: "Find your QR Code",
      desc: "Under \"Quick Login QR Code\", you'll see a unique QR code generated for your account.",
      icon: <QrCode className="h-8 w-8" />,
    },
    {
      step: 3,
      title: "Scan with your phone",
      desc: "Open your phone camera and point it at the QR code. It will open a page asking for your email and password — this is a one-time registration.",
      icon: <Smartphone className="h-8 w-8" />,
    },
    {
      step: 4,
      title: "You're registered!",
      desc: "Your phone now has a device token saved. You'll see it listed under \"Registered Devices\" in Settings.",
      icon: <Check className="h-8 w-8" />,
    },
    {
      step: 5,
      title: "Next time: instant login",
      desc: "On the desktop login page, scan the QR code with your registered phone. Your phone auto-confirms and the desktop logs in — no password needed!",
      icon: <Sparkles className="h-8 w-8" />,
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden [&>button]:hidden" data-testid="hotfix-update-popup"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: 'var(--amber-glow)' }}>
              <Sparkles className="h-6 w-6" style={{ color: 'var(--amber)' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>
                What's New — April 2026
              </h2>
              <p className="text-xs" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fm)' }}>{UPDATE_VERSION}</p>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            {['Changelog', 'QR Login Setup Guide'].map((label, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: page === i ? 'var(--amber-glow)' : 'transparent',
                  color: page === i ? 'var(--amber)' : 'var(--text-muted)',
                  fontFamily: 'var(--fd)',
                  letterSpacing: '0.03em',
                }}
                data-testid={`update-tab-${i}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="max-h-[50vh] px-6 pb-4">
          {page === 0 ? (
            <div className="space-y-3 pt-3">
              {updates.map((u, i) => (
                <div key={i} className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
                  <div className="flex items-center gap-3 px-4 py-2.5" style={{ background: u.bg }}>
                    <div className="p-1 rounded-md" style={{ color: u.color }}>{u.icon}</div>
                    <h3 className="text-sm font-bold" style={{ color: u.color, fontFamily: 'var(--fd)', letterSpacing: '0.02em' }}>{u.title}</h3>
                  </div>
                  <div className="px-4 py-3" style={{ background: 'var(--bg-raised)' }}>
                    <ul className="space-y-1.5">
                      {u.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <ChevronRight className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4 pt-4">
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Set up QR login in 5 simple steps. Once done, you'll never need to type your password on desktop again.
              </p>
              {tutorialSteps.map((s) => (
                <div key={s.step} className="flex gap-4 items-start">
                  <div className="flex flex-col items-center gap-1 shrink-0">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--amber-glow)', color: 'var(--amber)' }}>
                      {s.icon}
                    </div>
                    {s.step < tutorialSteps.length && <div className="w-px h-6" style={{ background: 'var(--border-color)' }} />}
                  </div>
                  <div className="pt-1">
                    <h4 className="text-sm font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>
                      Step {s.step}: {s.title}
                    </h4>
                    <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{s.desc}</p>
                  </div>
                </div>
              ))}
              <div className="rounded-lg p-4 mt-2" style={{ background: 'var(--amber-glow)', border: '1px solid rgba(245,166,35,0.3)' }}>
                <p className="text-sm font-medium" style={{ color: 'var(--amber)' }}>
                  Tip: You can revoke any device anytime from Settings → Registered Devices. If you lose your phone, just revoke it and re-register a new one.
                </p>
              </div>
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap" style={{ borderTop: '1px solid var(--border-color)', background: 'var(--bg-raised)' }}>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none" style={{ color: 'var(--text-secondary)' }}>
            <Checkbox
              checked={dontShowAgain}
              onCheckedChange={(v) => setDontShowAgain(v === true)}
              data-testid="hotfix-dont-show-again"
            />
            <span>Don't show this again</span>
          </label>
          <Button
            onClick={handleClose}
            className="font-bold uppercase tracking-wider hover:brightness-110"
            style={{ background: 'var(--amber)', color: 'var(--bg-void)', fontFamily: 'var(--fd)', letterSpacing: '0.05em' }}
            data-testid="dismiss-hotfix-btn"
          >
            {dontShowAgain ? 'Got it — close' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
